import React, { useCallback, useRef } from 'react';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionRankDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfoProps,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { SectionRankItem } from 'app-components/home/sections/items/SectionRankItem';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { SectionTitle } from 'app-components/home/sections/components/SectionTitle';
import { getValidContentAndProfile } from 'utils/sectionContentUtils';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useSlide } from 'ui-hooks/useSlide';
import { tm } from 'utils/tm';

type SectionProps = {
  section: SectionRankDto;
  contents: Record<number, ContentRelayDto>;
  profileLites: Record<number, ProfileLiteRelayDto>;
  showCategory?: boolean;
  showViewCount?: boolean;
};

const Height = 150;
const VerticalGap = 8;
const HorizontalGap = 24;

export const SectionRanks = ({
  section,
  contents,
  profileLites,
  showCategory = false,
  showViewCount = false,
  trackerExtraInfo,
}: SectionProps & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const blockedProfileIds = useBlockProfiles();
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const handleOnClickTracker = useCallback(
    (contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentListItem, {
        listType: ContentListType.SectionContent,
        contentId,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  if (section.contentIds.length === 0) {
    return null;
  }
  const height = Height * section.rowCount + VerticalGap * (section.rowCount - 1);

  return (
    <div>
      <SectionTitle section={section} moreLink={section.moreLink} trackerExtraInfo={trackerExtraInfo} />
      <div className="relative">
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
        <div
          ref={slideRef}
          onScroll={handleSlide}
          role="button"
          tabIndex={0}
          className={tm(
            'relative flex flex-col flex-wrap content-start overflow-x-scroll overflow-y-hidden px-4 gap-y-2 gap-x-6',
            'pc:overflow-x-hidden pc:px-0 pc:mx-[15px]',
            'dynamic-h [&::-webkit-scrollbar]:hidden',
          )}
          style={{ '--dynamic-h': `${height}px` } as React.CSSProperties}>
          {section.contentIds.map((contentId, index) => {
            const validationResult = getValidContentAndProfile(contentId, contents, profileLites, blockedProfileIds);
            if (!validationResult) {
              return null;
            }

            const { content, profile } = validationResult;

            return (
              <SectionRankItem
                key={contentId}
                content={content}
                profile={profile}
                rank={index + 1}
                showCategory={showCategory}
                showViewCount={showViewCount}
                onClick={() => handleOnClickTracker(contentId, index)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SectionRanks;
